<div class="ua-container">

  <!-- ── Toolbar ────────────────────────────────────────────────────────────── -->
  <div class="ua-toolbar">
    <span class="ua-title">User Accounts</span>
    <span class="ua-count mat-body-2" *ngIf="dataSource.data.length">
      {{ dataSource.data.length }} account(s)
    </span>

    <!-- Bulk destroy (rights-gated) -->
    <button mat-stroked-button
            *ngIf="canDestroyResources"
            (click)="destroyAllAccounts()"
            [disabled]="!dataSource.data.length || isBulkBusy || isLoading">
      <mat-icon color="warn">delete_sweep</mat-icon>
      <span class="ua-danger-text">Destroy All Accounts</span>
    </button>

    <button mat-stroked-button (click)="refreshRequested.emit()" [disabled]="isLoading || isBulkBusy">
      <mat-icon>refresh</mat-icon> Refresh
    </button>

    <button mat-stroked-button (click)="exportCsv()" [disabled]="!dataSource.data.length">
      <mat-icon>download</mat-icon> Export CSV
    </button>
  </div>

  <!-- ── Bulk loading bar ────────────────────────────────────────────────────── -->
  <mat-progress-bar *ngIf="isBulkBusy" mode="indeterminate" class="ua-bulk-bar"></mat-progress-bar>

  <!-- ── Loading state ──────────────────────────────────────────────────────── -->
  <div *ngIf="isLoading" class="ua-state-row">
    <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
    <span>Loading user accounts…</span>
  </div>

  <!-- ── Error state ────────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && loadError" class="ua-error-row">
    <mat-icon>error_outline</mat-icon>
    <span>{{ loadError }}</span>
    <button mat-stroked-button (click)="refreshRequested.emit()">Retry</button>
  </div>

  <!-- ── Empty state ────────────────────────────────────────────────────────── -->
  <div *ngIf="!isLoading && !loadError && !dataSource.data.length" class="ua-empty">
    <mat-icon class="ua-empty-icon">manage_accounts</mat-icon>
    <p>No user accounts have been provisioned for this class.</p>
  </div>

  <!-- ── Table ──────────────────────────────────────────────────────────────── -->
  <div class="ua-table-wrap" *ngIf="!isLoading && !loadError && dataSource.data.length">
    <table mat-table [dataSource]="dataSource" class="ua-table">

      <!-- Participant -->
      <ng-container matColumnDef="participant">
        <th mat-header-cell *matHeaderCellDef>Participant</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.participant">{{ row.participant ?? '—' }}</td>
      </ng-container>

      <!-- Invite Email -->
      <ng-container matColumnDef="inviteEmail">
        <th mat-header-cell *matHeaderCellDef>Invite Email</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.inviteEmail">{{ truncate(row.inviteEmail, 22) }}</td>
      </ng-container>

      <!-- Redeem Email -->
      <ng-container matColumnDef="redeemEmail">
        <th mat-header-cell *matHeaderCellDef>Redeem Email</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.redeemEmail">{{ truncate(row.redeemEmail, 22) }}</td>
      </ng-container>

      <!-- Cloud Username -->
      <ng-container matColumnDef="cloudUsername">
        <th mat-header-cell *matHeaderCellDef>Cloud Username</th>
        <td mat-cell *matCellDef="let row">{{ row.cloudUsername ?? '—' }}</td>
      </ng-container>

      <!-- Cloud Password — masked with per-row reveal toggle -->
      <ng-container matColumnDef="cloudPassword">
        <th mat-header-cell *matHeaderCellDef>Password</th>
        <td mat-cell *matCellDef="let row">
          <span class="ua-password-value">
            <ng-container *ngIf="isRevealed(row)">{{ row.cloudPassword ?? row.password ?? '—' }}</ng-container>
            <ng-container *ngIf="!isRevealed(row)">••••••••</ng-container>
          </span>
          <button mat-icon-button class="ua-reveal-btn"
                  [matTooltip]="isRevealed(row) ? 'Hide password' : 'Reveal password'"
                  (click)="toggleReveal(row)">
            <mat-icon>{{ isRevealed(row) ? 'visibility_off' : 'visibility' }}</mat-icon>
          </button>
        </td>
      </ng-container>

      <!-- Subscription -->
      <ng-container matColumnDef="subscriptionId">
        <th mat-header-cell *matHeaderCellDef>Subscription</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.subscriptionId">{{ truncate(row.subscriptionId) }}</td>
      </ng-container>

      <!-- Description -->
      <ng-container matColumnDef="description">
        <th mat-header-cell *matHeaderCellDef>Description</th>
        <td mat-cell *matCellDef="let row" class="truncate-cell"
            [matTooltip]="row.description">{{ truncate(row.description) }}</td>
      </ng-container>

      <!-- Actions -->
      <ng-container matColumnDef="actions">
        <th mat-header-cell *matHeaderCellDef class="ua-actions-header">Actions</th>
        <td mat-cell *matCellDef="let row" class="ua-actions-cell">
          <button mat-icon-button
                  [matMenuTriggerFor]="rowMenu"
                  [disabled]="isBusy(row) || isBulkBusy"
                  matTooltip="Actions">
            <mat-icon>more_vert</mat-icon>
          </button>
          <mat-menu #rowMenu="matMenu">
            <button mat-menu-item *ngIf="canDestroyResources" (click)="destroyAccount(row)">
              <mat-icon color="warn">delete_forever</mat-icon>
              <span class="ua-danger-text">Destroy Account</span>
            </button>
            <button mat-menu-item *ngIf="!canDestroyResources" disabled>
              <mat-icon>lock</mat-icon> No destructive actions
            </button>
          </mat-menu>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
      <tr mat-row *matRowDef="let row; columns: displayedColumns;"
          [class.ua-row-busy]="isBusy(row)"></tr>
    </table>

    <mat-paginator
      [pageSizeOptions]="pageSizeOptions"
      [pageSize]="pageSize"
      appPersistPageSize="class:userAccounts:list"
      showFirstLastButtons>
    </mat-paginator>
  </div>

</div>
